import { DataType } from '../components/types';
import { Cell, Entity } from '../context-selector';
import { ContextSelectorProps, TableRow } from '../context-selector-v2';

export enum ConfigItemType {
  Select = 'select',
  MultiSelect = 'multiSelect',
  NumericRange = 'numericRange',
  DateRange = 'dateRange',
  MultiCell = 'multiCell',
  DataTypeControl = 'dataTypeControl',
  Group = 'group',
  ContextSelector = 'contextSelector',
}

export type AtomicConfigItemType = Exclude<ConfigItemType, ConfigItemType.Group>;

type TypedConfigItem<T extends ConfigItemType, U extends ConfigValue> = {
  key: string;
  label: string;
  type: T;
  value?: U;
  description?: string; // something for the UI, if supported by UI controls
  placeholder?: string; // something for the UI, if supported by UI controls
  disabled?: boolean; // something for the UI, if supported by UI controls,
  tooltipInfo?: string; // something for the UI, if supported by UI controls
};

// ContextSelector config  item type

export type ContextSelectorValue = TableRow[];

export type ContextSelectorConfigItem = TypedConfigItem<ConfigItemType.ContextSelector, ContextSelectorValue> & {
  options: ContextSelectorProps;
};

export type SelectConfigValue = {
  value: string;
  label?: string;
};

export type SelectConfigValueOption = SelectConfigValue & {
  label: string; // required for the UI
  disabled?: boolean;
};

export type SelectConfigItem = TypedConfigItem<ConfigItemType.Select, SelectConfigValue> & {
  /**
   * The options to select from (array of objects with value and label)
   */
  options: SelectConfigValueOption[];
  /**
   * `true` to allow the user type in order to limit options
   */
  combobox?: boolean;
  /**
   * If true, indicates that options are being loaded
   */
  isLoading?: boolean;
};

export type MultiSelectConfigValue = SelectConfigValue[];

export type MultiSelectConfigItem = TypedConfigItem<ConfigItemType.MultiSelect, MultiSelectConfigValue> & {
  /**
   * The options to select from (array of objects with value and label)
   */
  options: SelectConfigValueOption[];
  /**
   * `true` to allow the user type in order to limit options
   */
  combobox?: boolean;
  /**
   * If true, the options to "select all" and "unselect all" are shown
   */
  showSelectAllOptions?: boolean;
  /**
   * If true, indicates that options are being loaded
   */
  isLoading?: boolean;
};

export type NumericRangeConfigValue = {
  /**
   * The minimum value of the range
   */
  min?: number;
  /**
   * The maximum value of the range
   */
  max?: number;
};

export type NumericRangeConfigItem = TypedConfigItem<ConfigItemType.NumericRange, NumericRangeConfigValue> & {
  options?: NumericRangeConfigValue;
};

export type DateRangeConfigValue = {
  /**
   * The minimum value of the range
   */
  min?: Date;
  /**
   * The maximum value of the range
   */
  max?: Date;
};

export type DateRangeConfigItem = TypedConfigItem<ConfigItemType.DateRange, DateRangeConfigValue> & {
  options?: DateRangeConfigValue;
};

export type MultiCellConfigValue = Entity[];

export type MultiCellConfigItem = TypedConfigItem<ConfigItemType.MultiCell, MultiCellConfigValue> & {
  /**
   * Array of multi-cell options
   */
  options: Cell[];
};

export type DataTypeControlConfigValue = DataType;

export type DataTypeControlConfigItem = Pick<
  TypedConfigItem<ConfigItemType.DataTypeControl, DataTypeControlConfigValue>,
  'key' | 'type' | 'value' | 'disabled'
>;

export type ConfigGroupValue = ConfigItem[];

export type ConfigGroup = TypedConfigItem<ConfigItemType.Group, ConfigGroupValue>;

export type ConfigItem =
  | ConfigGroup
  | SelectConfigItem
  | MultiSelectConfigItem
  | NumericRangeConfigItem
  | DateRangeConfigItem
  | MultiCellConfigItem
  | DataTypeControlConfigItem
  | ContextSelectorConfigItem;

export type ConfigItemWithOptions =
  | SelectConfigItem
  | MultiSelectConfigItem
  | NumericRangeConfigItem
  | DateRangeConfigItem
  | MultiCellConfigItem;

export type ConfigValue =
  | ConfigGroupValue
  | SelectConfigValue
  | MultiSelectConfigValue
  | NumericRangeConfigValue
  | DateRangeConfigValue
  | MultiCellConfigValue
  | DataTypeControlConfigValue
  | ContextSelectorValue;

export type ConfigItemValue = Pick<ConfigItem, 'key' | 'value'>;
export type ConfigItemOptions = Pick<ConfigItemWithOptions, 'key' | 'options'>;

// type guards
export function hasOptions(item: ConfigItem): item is ConfigItemWithOptions {
  return Object.keys(item).includes('options');
}

export function isSelect(item: ConfigItem): item is SelectConfigItem {
  return item.type === ConfigItemType.Select;
}

export function isMultiSelect(item: ConfigItem): item is MultiSelectConfigItem {
  return item.type === ConfigItemType.MultiSelect;
}

export function isNumericRange(item: ConfigItem): item is NumericRangeConfigItem {
  return item.type === ConfigItemType.NumericRange;
}

export function isDateRange(item: ConfigItem): item is DateRangeConfigItem {
  return item.type === ConfigItemType.DateRange;
}

export function isMultiCell(item: ConfigItem): item is MultiCellConfigItem {
  return item.type === ConfigItemType.MultiCell;
}

export function isDataTypeControl(item: ConfigItem): item is DataTypeControlConfigItem {
  return item.type === ConfigItemType.DataTypeControl;
}

export function isConfigGroup(item: ConfigItem): item is ConfigGroup {
  return item.type === ConfigItemType.Group;
}
